export type DatabricksCapabilityStatus = "stable" | "beta" | "partial" | "planned";
export type DatabricksSupportTier = "first-party" | "managed-recipe" | "escape-hatch";
export type DatabricksUpstreamMaturity = "general-availability" | "beta" | "preview" | "unknown";
export type DatabricksApiFidelity =
  | "native-complete"
  | "native-subset"
  | "fabric-abstraction"
  | "not-supported";
export type DatabricksLifecycleOperation =
  | "discover"
  | "read"
  | "query"
  | "execute"
  | "create"
  | "update"
  | "delete"
  | "recover";
export type DatabricksCertificationLevel =
  | "contract"
  | "protected-live"
  | "scheduled-live"
  | "not-configured";

export interface DatabricksCapability {
  id: string;
  name: string;
  category: "agent" | "data" | "ml" | "deployment" | "governance" | "operations";
  status: DatabricksCapabilityStatus;
  tier: DatabricksSupportTier;
  certification: DatabricksCertificationLevel;
  auth: ReadonlyArray<"oauth-m2m" | "app-service-principal" | "on-behalf-of" | "pat">;
  /** API contract targets. This does not imply retained live evidence for every cloud. */
  contractClouds: readonly DatabricksCapabilityCloud[];
  /** Clouds represented by linked retained live evidence. */
  clouds: readonly DatabricksCapabilityCloud[];
  surfaces?: ReadonlyArray<{ package: "@fabric-harness/databricks"; exports: readonly string[] }>;
  /** Capability-specific checks that linked evidence must satisfy. */
  requiredEvidence: readonly string[];
  /** Opaque references resolved by the published compatibility matrix/evidence store. */
  evidenceIds?: readonly string[];
  docs: string;
  notes?: string;
  /**
   * Independent coverage dimensions. This is the authoritative compatibility view; `status`
   * remains only a compact release label.
   */
  coverage: {
    upstreamMaturity: DatabricksUpstreamMaturity;
    apiFidelity: DatabricksApiFidelity;
    lifecycle: readonly DatabricksLifecycleOperation[];
    runtimeCertification: DatabricksCertificationLevel;
    identities: DatabricksCapability["auth"];
    contractClouds: readonly DatabricksCapabilityCloud[];
    certifiedClouds: readonly DatabricksCapabilityCloud[];
    knownGaps: readonly string[];
  };
}

const ALL_CLOUDS = ["aws", "azure", "gcp"] as const;
export type DatabricksCapabilityCloud = (typeof ALL_CLOUDS)[number];

export type DatabricksPlatformDomainId =
  | "agents-and-generative-ai"
  | "machine-learning"
  | "data-engineering"
  | "sql-and-bi"
  | "apps-and-operational-data"
  | "governance-and-security"
  | "sharing-and-collaboration"
  | "operations-and-finops";

export interface DatabricksPlatformDomain {
  id: DatabricksPlatformDomainId;
  name: string;
  capabilityIds: readonly string[];
  /** Native Databricks areas not yet represented by a supported Harness surface. */
  knownGaps: readonly string[];
}

/**
 * Whole-platform inventory. A domain remains listed even when its current Harness coverage is
 * empty, preventing documentation and release claims from silently omitting native Databricks
 * product families.
 */
export const DATABRICKS_PLATFORM_DOMAINS: readonly DatabricksPlatformDomain[] = [
  {
    id: "agents-and-generative-ai",
    name: "Agents and generative AI",
    capabilityIds: [
      "responses-agent",
      "unity-ai-gateway",
      "agent-services",
      "supervisor-agents",
      "mcp-multi-agent",
      "managed-memory",
      "ai-search",
      "mlflow-genai",
    ],
    knownGaps: [
      "Supervisor Agents and managed memory have contract coverage only; protected live evidence is not yet retained.",
    ],
  },
  {
    id: "machine-learning",
    name: "Machine learning and serving",
    capabilityIds: [
      "model-lifecycle",
      "serving-admin",
      "provisioned-throughput-admin",
      "feature-serving",
      "ai-functions",
    ],
    knownGaps: [
      "Training, AutoML, Feature Engineering authoring, batch inference, and full registered-model lifecycle are not yet represented.",
    ],
  },
  {
    id: "data-engineering",
    name: "Data engineering and orchestration",
    capabilityIds: [
      "jobs-run",
      "jobs-authoring",
      "lakeflow",
      "lakeflow-authoring",
      "automation-bundles",
    ],
    knownGaps: [
      "Spark Connect, streaming tables, materialized views, and ingestion connectors do not yet have first-party Harness APIs.",
    ],
  },
  {
    id: "sql-and-bi",
    name: "SQL, dashboards, and AI/BI",
    capabilityIds: ["sql-warehouse", "genie-query", "genie-authoring", "genie-agent-mode"],
    knownGaps: [
      "Dashboard, query, alert, warehouse, and metric-view authoring are not yet complete.",
    ],
  },
  {
    id: "apps-and-operational-data",
    name: "Apps and operational data",
    capabilityIds: ["apps-deploy", "lakebase", "uc-volumes"],
    knownGaps: [
      "All App resource kinds need typed first-party configuration; AI Search and Genie are currently the explicit convenience bindings.",
    ],
  },
  {
    id: "governance-and-security",
    name: "Governance, identity, and security",
    capabilityIds: [
      "unity-catalog-read",
      "uc-admin",
      "secrets-write",
      "governance-approvals",
      "workspace-write",
      "workspace-api",
    ],
    knownGaps: [
      "Lineage reads, ABAC policy authoring, row filters, masks, federation, account-level IAM, and system-table administration remain incomplete.",
    ],
  },
  {
    id: "sharing-and-collaboration",
    name: "Sharing and collaboration",
    capabilityIds: [],
    knownGaps: [
      "Delta Sharing, Clean Rooms, Marketplace, and data-room lifecycle are not yet supported.",
    ],
  },
  {
    id: "operations-and-finops",
    name: "Operations, observability, and FinOps",
    capabilityIds: ["consumption-cost", "mlflow-genai", "automation-bundles"],
    knownGaps: [
      "Workspace system-table coverage, budgets, billing APIs, audit-log delivery, and fleet-wide administration remain incomplete.",
    ],
  },
] as const;

export interface DatabricksCapabilityEvidenceReference {
  id: string;
  checks: readonly string[];
  source: string;
  certifiedAt: string;
  packageVersion: string;
  artifactSha256: string;
  apiVersions: Readonly<Record<string, string>>;
  environments: ReadonlyArray<{
    cloud: DatabricksCapabilityCloud;
    region: string;
    authMode: string;
  }>;
}

const AUTHORING_EVIDENCE_ID =
  "dbx-cert-d4b16490526892f3945ba5a53249c600581aaed105829c0b87f00eed508bcb18";
const CLASSIC_AUTHORING_EVIDENCE_ID =
  "dbx-cert-d5bdbd276533a8634910e223898337314446561be2a444030fc2d11baa237bec";
const CURRENT_CONSUMPTION_EVIDENCE_ID =
  "dbx-cert-69878cfdd583d1f4331487a5af3272ae7380736914203dcba3bbb8b4d87adb34";

export const DATABRICKS_CAPABILITY_EVIDENCE: readonly DatabricksCapabilityEvidenceReference[] = [
  {
    id: CURRENT_CONSUMPTION_EVIDENCE_ID,
    source: "repository-evidence:artifacts/databricks-certification-current.json",
    certifiedAt: "2026-07-21T06:14:56.810Z",
    packageVersion: "4.9.1",
    artifactSha256: "5e7b9f0a26e0834bcbf703561aeca46f3e48da5be6dd5022084f723bde630ab1",
    apiVersions: {
      sdkJs: "0.21.0",
      jobs: "2.2",
      sqlStatements: "2.0",
      unityCatalog: "2.1",
      workspace: "2.0",
      mlflow: "2.0",
      lakebaseCredentials: "2.0",
      apps: "2.0",
      responses: "agent/v1/responses",
      aiSearch: "2.0",
      serving: "2.0",
      lakeflow: "2.0",
      genie: "2.0",
      mcp: "2.0",
    },
    environments: [{ cloud: "azure", region: "eastus2", authMode: "oauth-m2m" }],
    checks: [
      "identity",
      "ai-gateway.discover",
      "ai-gateway.invoke",
      "sql.execute",
      "sql.uc-allowed",
      "sql.uc-denied",
      "jobs.run",
      "jobs.wait",
      "jobs.output",
      "lakebase.read",
      "lakebase.write",
      "lakebase.recovery",
      "volumes.read",
      "volumes.write",
      "volumes.authorization-denied",
      "uc.read",
      "uc.authorization-denied",
      "cost.read",
      "cost.reconcile",
      "approval.request",
      "approval.consume",
      "approval.denied",
      "lineage.write",
      "apps.build",
      "apps.deploy",
      "apps.identity",
      "apps.restart",
      "genie.execute",
      "genie.authorization-denied",
      "mcp.invoke",
      "mcp.authorization-denied",
      "mcp.obo",
      "mcp.token-redaction",
      "feature-serving.read",
      "feature-serving.authorization-denied",
      "ai-search.query",
      "ai-search.authorization-denied",
      "lakeflow.read",
      "lakeflow.execute",
      "responses.execute",
      "responses.recovery",
    ],
  },
  {
    id: "dbx-cert-d03df5a0a5b69a98d8acb0ad4c1e9ef9bcffefea7e433cbd2ba0ff3d792fec71",
    source: "github-actions-run:29173178927",
    certifiedAt: "2026-07-11T03:54:35.385Z",
    packageVersion: "1.2.0",
    artifactSha256: "25cc6f6a0f7c912ef0297a4295d5f455a48d5ebcb185f9217fbd222a0b11bdfe",
    apiVersions: {
      jobs: "2.1",
      sqlStatements: "2.0",
      unityCatalog: "2.1",
      workspace: "2.0",
      mlflow: "2.0",
      lakebaseCredentials: "2.0",
      apps: "2.0",
    },
    environments: [{ cloud: "azure", region: "eastus2", authMode: "oauth-m2m" }],
    checks: [
      "ai-gateway.discover",
      "ai-gateway.invoke",
      "mlflow.trace",
      "mlflow.evaluate",
      "ai-search.query",
      "ai-search.authorization-denied",
      "lakeflow.read",
      "lakeflow.execute",
      "apps.build",
      "apps.deploy",
      "sql.execute",
      "sql.uc-allowed",
      "sql.uc-denied",
      "jobs.run",
      "jobs.wait",
      "jobs.output",
      "lakebase.read",
      "lakebase.write",
      "lakebase.recovery",
      "volumes.read",
      "volumes.write",
      "volumes.authorization-denied",
      "uc.read",
      "uc.authorization-denied",
      "cost.read",
      "cost.reconcile",
      "approval.request",
      "approval.consume",
      "approval.denied",
      "lineage.write",
      "apps.identity",
      "genie.execute",
      "genie.authorization-denied",
      "feature-serving.read",
      "feature-serving.authorization-denied",
      "ai-functions.execute",
      "ai-functions.authorization-denied",
    ],
  },
  {
    id: AUTHORING_EVIDENCE_ID,
    source: "repository-evidence:artifacts/databricks-authoring-certification-current.json",
    certifiedAt: "2026-07-20T10:33:24.197Z",
    packageVersion: "4.4.1",
    artifactSha256: "52e9a74de75da3f51a1f18c0cc59213474ef6b7ee6143885129c14b24cde9e4b",
    apiVersions: {
      jobs: "2.2",
      sqlStatements: "2.0",
      unityCatalog: "2.1",
      workspace: "2.0",
      mlflow: "2.0",
      lakebaseCredentials: "2.0",
      apps: "2.0",
      responses: "agent/v1/responses",
      aiSearch: "2.0",
      serving: "2.0",
      lakeflow: "2.0",
      genie: "2.0",
      mcp: "2.0",
    },
    environments: [{ cloud: "azure", region: "eastus2", authMode: "on-behalf-of" }],
    checks: [
      "identity",
      "approval.request",
      "approval.consume",
      "lineage.write",
      "jobs.create",
      "jobs.verify",
      "jobs.mutate",
      "jobs.delete",
      "lakeflow.create",
      "lakeflow.verify",
      "lakeflow.mutate",
      "lakeflow.delete",
      "ai-search-admin.create",
      "ai-search-admin.verify",
      "ai-search-admin.mutate",
      "ai-search-admin.delete",
      "serving.create",
      "serving.verify",
      "serving.mutate",
      "serving.delete",
      "uc-admin.create",
      "uc-admin.verify",
      "uc-admin.mutate",
      "uc-admin.delete",
      "uc-admin.obo-execution",
      "workspace.create",
      "workspace.verify",
      "workspace.mutate",
      "workspace.delete",
      "secrets.create",
      "secrets.verify",
      "secrets.mutate",
      "secrets.delete",
      "genie-authoring.create",
      "genie-authoring.verify",
      "genie-authoring.mutate",
      "genie-authoring.query",
      "genie-authoring.delete",
    ],
  },
  {
    id: CLASSIC_AUTHORING_EVIDENCE_ID,
    source: "github-actions-run:29660486237",
    certifiedAt: "2026-07-18T20:52:27.286Z",
    packageVersion: "4.0.2",
    artifactSha256: "72f602626b9d79a808f6dd9e30f6ba703061bda19bae2aef983e7e92d7c61a45",
    apiVersions: {
      jobs: "2.2",
      sqlStatements: "2.0",
      unityCatalog: "2.1",
      workspace: "2.0",
      mlflow: "2.0",
      lakebaseCredentials: "2.0",
      apps: "2.0",
      responses: "agent/v1/responses",
      aiSearch: "2.0",
      mcp: "2.0",
    },
    environments: [{ cloud: "azure", region: "westus3", authMode: "on-behalf-of" }],
    checks: [
      "identity",
      "approval.consume",
      "lineage.write",
      "jobs.create",
      "jobs.verify",
      "jobs.mutate",
      "jobs.delete",
    ],
  },
] as const;

/**
 * Source of truth for public Databricks workload claims. Package tests enforce
 * its invariants; a capability is not stable merely because an API wrapper exists.
 */
type DatabricksCapabilityDefinition = Omit<DatabricksCapability, "clouds" | "coverage"> & {
  upstreamMaturity?: DatabricksUpstreamMaturity;
  apiFidelity?: DatabricksApiFidelity;
  lifecycle?: readonly DatabricksLifecycleOperation[];
  knownGaps?: readonly string[];
};

const DATABRICKS_CAPABILITY_DEFINITIONS: readonly DatabricksCapabilityDefinition[] = [
  {
    id: "responses-agent",
    name: "Responses API and MLflow ResponsesAgent",
    category: "agent",
    status: "beta",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["databricksApp"] }],
    requiredEvidence: ["responses.execute", "responses.recovery"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/databricks/responses-agent",
    notes:
      "The API remains Beta, but execution, streaming output, inference-table configuration and recovery passed on the exact 4.9.1 package and App artifacts.",
  },
  {
    id: "unity-ai-gateway",
    name: "Unity AI Gateway model services",
    category: "governance",
    status: "beta",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricks", "databricksFoundationModelProvider"],
      },
    ],
    requiredEvidence: ["ai-gateway.discover", "ai-gateway.invoke"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/databricks/integrations",
  },
  {
    id: "agent-services",
    name: "Unity Catalog Agent Services",
    category: "governance",
    status: "beta",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["databricks"] }],
    requiredEvidence: [
      "agent-services.create",
      "agent-services.verify",
      "agent-services.mutate",
      "agent-services.delete",
    ],
    docs: "/docs/databricks/agent-services",
    notes:
      "Registration and permissions only; Databricks does not expose runtime invocation during the Beta.",
  },
  {
    id: "mcp-multi-agent",
    name: "Databricks MCP and governed multi-agent composition",
    category: "agent",
    status: "beta",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: [
          "defineDatabricksAgent",
          "databricksWithManagedMcp",
          "connectDatabricksManagedMcpServer",
        ],
      },
    ],
    requiredEvidence: ["mcp.invoke", "mcp.authorization-denied", "mcp.obo", "mcp.token-redaction"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/databricks/integrations",
    notes:
      "The first-party adapter, governed context boundary, OBO invocation, and token redaction passed Tier R on the exact 4.9.1 package. Status remains Beta while Databricks managed MCP surfaces are workspace-dependent previews.",
  },
  {
    id: "supervisor-agents",
    name: "Supervisor Agents and Knowledge Assistants",
    category: "agent",
    status: "beta",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["DatabricksSupervisorAgents", "databricksManagedAgentTool"],
      },
    ],
    requiredEvidence: [
      "supervisor-agents.discover",
      "supervisor-agents.invoke",
      "supervisor-agents.cancellation",
      "supervisor-agents.knowledge-assistant",
    ],
    docs: "/docs/databricks/agent-services",
    upstreamMaturity: "beta",
    apiFidelity: "native-subset",
    lifecycle: ["discover", "read", "query", "create", "update", "delete"],
    knownGaps: [
      "The generated Beta SDK lifecycle is available only after explicit acknowledgement; grants and protected-live evidence remain outstanding.",
    ],
    notes:
      "Harness discovers and invokes native managed agents without claiming ownership of their internal loop. Invocation uses the Responses-compatible serving endpoint resolved by the official SDK.",
  },
  {
    id: "managed-memory",
    name: "Databricks managed agent memory",
    category: "agent",
    status: "beta",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["DatabricksManagedMemoryClient"],
      },
    ],
    docs: "/docs/databricks/agent-services",
    requiredEvidence: [
      "managed-memory.read",
      "managed-memory.write",
      "managed-memory.scope-isolation",
      "managed-memory.delete",
    ],
    upstreamMaturity: "beta",
    apiFidelity: "native-subset",
    lifecycle: ["discover", "read", "query", "create", "update", "delete"],
    knownGaps: [
      "Databricks does not yet provide this API in the generated TypeScript SDK; the reviewed raw protocol adapter has contract tests but no retained protected-live evidence.",
    ],
    notes:
      "Every entry operation requires an application-supplied trusted scope. Harness session memory and Databricks managed memory remain separate retention choices.",
  },
  {
    id: "mlflow-genai",
    name: "MLflow GenAI tracing, evaluation and monitoring",
    category: "operations",
    status: "partial",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: [
          "databricks",
          "scoreRagTurn",
          "databricksMlflowLogMetricTool",
          "databricksMlflowLogParamTool",
        ],
      },
    ],
    requiredEvidence: ["mlflow.trace", "mlflow.evaluate"],
    evidenceIds: ["dbx-cert-d03df5a0a5b69a98d8acb0ad4c1e9ef9bcffefea7e433cbd2ba0ff3d792fec71"],
    docs: "/docs/databricks/rag",
    notes: "Reusable scorers, labeling sessions, conversation simulation and monitoring remain.",
  },
  {
    id: "ai-search",
    name: "Databricks AI Search",
    category: "data",
    status: "partial",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricksAiSearch", "DatabricksAiSearchAdmin"],
      },
    ],
    requiredEvidence: ["ai-search.query", "ai-search.authorization-denied"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/databricks/rag",
    upstreamMaturity: "general-availability",
    apiFidelity: "native-complete",
    lifecycle: ["query"],
    knownGaps: [
      "Hybrid, full-text, reranking, facets, sorting, thresholds, and pagination have contract evidence but still require exact-candidate live certification.",
    ],
    notes: "Query evidence remains separate from the AI Search administration lifecycle.",
  },
  {
    id: "lakeflow",
    name: "Lakeflow Declarative Pipelines",
    category: "data",
    status: "partial",
    tier: "managed-recipe",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricksLakeflowTools", "DatabricksLakeflowAuthoring"],
      },
    ],
    requiredEvidence: ["lakeflow.read", "lakeflow.execute"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/databricks/integrations",
    notes:
      "List/status are read-only defaults. Model-callable start/stop require a construction-time pipeline policy; read/execute evidence remains separate from authoring lifecycle evidence.",
  },
  {
    id: "automation-bundles",
    name: "Declarative Automation Bundles",
    category: "deployment",
    status: "partial",
    tier: "managed-recipe",
    certification: "protected-live",
    auth: ["oauth-m2m", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["databricksApp"] }],
    requiredEvidence: ["apps.build", "apps.deploy"],
    evidenceIds: [CURRENT_CONSUMPTION_EVIDENCE_ID],
    docs: "/docs/deployment/databricks",
    notes: "Typed multi-resource composition and schema-driven validation remain.",
  },
  {
    id: "model-lifecycle",
    name: "Model Registry, AI Runtime, batch inference and features",
    category: "ml",
    status: "planned",
    tier: "managed-recipe",
    certification: "not-configured",
    auth: ["oauth-m2m", "app-service-principal", "pat"],
    contractClouds: ALL_CLOUDS,
    docs: "/docs/databricks/workloads",
    requiredEvidence: ["model.create", "model.verify", "model.mutate", "model.delete"],
  },
  ...stableConsumptionCapabilities(),
  {
    id: "genie-authoring",
    name: "Genie Agent resource management",
    category: "data",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricks", "serializeDatabricksGenieAgentSpec"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: [
      "genie-authoring.create",
      "genie-authoring.verify",
      "genie-authoring.mutate",
      "genie-authoring.query",
      "genie-authoring.delete",
    ],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
    notes:
      "Protected Azure eastus2 evidence covers approval-bound create/query/update/trash with an empty cleanup ledger. Agent Mode streaming remains a separate Beta capability.",
  },
  {
    id: "genie-agent-mode",
    name: "Genie Agent Mode streaming",
    category: "data",
    status: "beta",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricks", "databricksGenieAgentModeTool"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["genie-agent-mode.stream", "genie-agent-mode.terminal"],
    notes:
      "The upstream API is Beta and requires explicit workspace preview enrollment. Credential-free SSE contracts pass; no stable promotion is implied.",
  },
  {
    id: "jobs-authoring",
    name: "Jobs authoring",
    category: "operations",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricksJobsAuthoring", "databricksJobsAuthoringTools"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["jobs.create", "jobs.verify", "jobs.mutate", "jobs.delete"],
    evidenceIds: [AUTHORING_EVIDENCE_ID, CLASSIC_AUTHORING_EVIDENCE_ID],
    notes:
      "Protected Azure evidence covers serverless STANDARD compute in eastus2 and policy-bound classic compute in westus3.",
  },
  {
    id: "lakeflow-authoring",
    name: "Lakeflow pipeline authoring",
    category: "data",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["DatabricksLakeflowAuthoring", "databricksLakeflowAuthoringTools"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["lakeflow.create", "lakeflow.verify", "lakeflow.mutate", "lakeflow.delete"],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
  },
  {
    id: "ai-search-admin",
    name: "AI Search endpoint and index administration",
    category: "data",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["DatabricksAiSearchAdmin", "databricksAiSearchAdminTools"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: [
      "ai-search-admin.create",
      "ai-search-admin.verify",
      "ai-search-admin.mutate",
      "ai-search-admin.delete",
    ],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
    notes:
      "Protected Azure eastus2 evidence covers the direct-vector lifecycle; delta-sync remains contract-tested and workspace-dependent.",
  },
  {
    id: "serving-admin",
    name: "Serving endpoint administration",
    category: "ml",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["DatabricksServingAdmin"] }],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["serving.create", "serving.verify", "serving.mutate", "serving.delete"],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
    notes:
      "Stable status covers non-preview custom-model serving; provisioned throughput and AI Gateway administration remain separate partial capabilities.",
  },
  {
    id: "provisioned-throughput-admin",
    name: "Provisioned throughput administration",
    category: "ml",
    status: "partial",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["DatabricksServingAdmin"] }],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["serving-pt.create", "serving-pt.verify", "serving-pt.delete"],
    notes: "Preview provenance is tracked separately from custom-model serving.",
  },
  {
    id: "serving-ai-gateway-admin",
    name: "Serving AI Gateway administration",
    category: "governance",
    status: "partial",
    tier: "first-party",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["DatabricksServingAdmin"] }],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["serving-gateway.verify", "serving-gateway.mutate"],
    notes: "Preview controls retain separate provenance from custom-model serving.",
  },
  {
    id: "uc-admin",
    name: "Unity Catalog administration",
    category: "governance",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports: ["DatabricksUnityCatalogAdmin"] }],
    docs: "/docs/databricks/authoring",
    requiredEvidence: [
      "uc-admin.create",
      "uc-admin.verify",
      "uc-admin.mutate",
      "uc-admin.delete",
      "uc-admin.obo-execution",
    ],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
    notes:
      "The agent-facing stable surface is restricted to Harness-managed resources and rejects force or cascade deletion.",
  },
  {
    id: "workspace-write",
    name: "Workspace object authoring",
    category: "operations",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricks"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: [
      "workspace.create",
      "workspace.verify",
      "workspace.mutate",
      "workspace.delete",
    ],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
  },
  {
    id: "workspace-api",
    name: "Native Databricks workspace REST escape hatch",
    category: "operations",
    status: "partial",
    tier: "escape-hatch",
    certification: "contract",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["databricksWorkspaceApi"],
      },
    ],
    docs: "/docs/databricks/native-access",
    requiredEvidence: ["workspace-api.contract"],
    apiFidelity: "native-complete",
    lifecycle: ["discover", "read", "query", "execute", "create", "update", "delete"],
    knownGaps: [
      "The escape hatch preserves HTTP fidelity but intentionally does not add domain schemas, governed ToolDefs, or lifecycle ownership.",
    ],
  },
  {
    id: "secrets-write",
    name: "Secret-reference administration",
    category: "governance",
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [
      {
        package: "@fabric-harness/databricks",
        exports: ["DatabricksSecretsAuthoring", "databricksSecretsAuthoringTools"],
      },
    ],
    docs: "/docs/databricks/authoring",
    requiredEvidence: ["secrets.create", "secrets.verify", "secrets.mutate", "secrets.delete"],
    evidenceIds: [AUTHORING_EVIDENCE_ID],
    notes:
      "Built-in ToolDefs accept SecretRef values only; raw secret values remain typed-client-only.",
  },
] as const;

/**
 * Public capability registry. `contractClouds` describes designed API
 * portability; `clouds` is deliberately narrower and is derived only from
 * retained evidence linked to that capability.
 */
export const DATABRICKS_CAPABILITIES: readonly DatabricksCapability[] =
  DATABRICKS_CAPABILITY_DEFINITIONS.map((definition) => {
    const { upstreamMaturity, apiFidelity, lifecycle, knownGaps, ...capability } = definition;
    const clouds = certifiedCloudsForEvidence(capability.evidenceIds);
    return {
      ...capability,
      clouds,
      coverage: {
        upstreamMaturity: upstreamMaturity ?? inferredUpstreamMaturity(capability.status),
        apiFidelity: apiFidelity ?? inferredApiFidelity(capability.status),
        lifecycle: lifecycle ?? inferredLifecycle(capability.requiredEvidence),
        runtimeCertification: capability.certification,
        identities: capability.auth,
        contractClouds: capability.contractClouds,
        certifiedClouds: clouds,
        knownGaps: knownGaps ?? [],
      },
    };
  });

function stableConsumptionCapabilities(): DatabricksCapabilityDefinition[] {
  const rows: Array<[string, string, DatabricksCapability["category"], string[], string[]]> = [
    [
      "sql-warehouse",
      "SQL Warehouse read and execution",
      "data",
      ["databricksSqlReadTool", "databricksSqlTool"],
      ["sql.execute", "sql.uc-allowed", "sql.uc-denied"],
    ],
    [
      "jobs-run",
      "Jobs run lifecycle",
      "operations",
      ["databricksJobs"],
      ["jobs.run", "jobs.wait", "jobs.output"],
    ],
    [
      "lakebase",
      "Lakebase persistence",
      "data",
      ["lakebaseClient", "databricksPersistence"],
      ["lakebase.read", "lakebase.write", "lakebase.recovery"],
    ],
    [
      "uc-volumes",
      "Unity Catalog Volumes attachments",
      "data",
      ["UcVolumesAttachmentStore"],
      ["volumes.read", "volumes.write", "volumes.authorization-denied"],
    ],
    [
      "unity-catalog-read",
      "Unity Catalog discovery",
      "governance",
      ["unityCatalogTablesTool", "databricksTableInfoTool"],
      ["uc.read", "uc.authorization-denied"],
    ],
    [
      "consumption-cost",
      "Consumption and cost attribution",
      "operations",
      ["databricksConsumption", "databricksActualCostSource"],
      ["cost.read", "cost.reconcile"],
    ],
    [
      "governance-approvals",
      "Governance and steward approvals",
      "governance",
      ["withGovernance", "databricksApprovalRules", "analyticsCopilotGovernance"],
      ["approval.request", "approval.consume", "approval.denied", "lineage.write"],
    ],
    [
      "apps-deploy",
      "Databricks Apps runtime",
      "deployment",
      ["databricksApp"],
      ["apps.build", "apps.deploy", "apps.identity"],
    ],
    [
      "genie-query",
      "Genie query",
      "data",
      ["databricksGenieTool"],
      ["genie.execute", "genie.authorization-denied"],
    ],
    [
      "feature-serving",
      "Feature Serving lookup",
      "ml",
      ["databricksFeatureLookupTool"],
      ["feature-serving.read", "feature-serving.authorization-denied"],
    ],
    [
      "ai-functions",
      "AI Functions query",
      "ml",
      ["databricksAiQueryTool"],
      ["ai-functions.execute", "ai-functions.authorization-denied"],
    ],
  ];
  return rows.map(([id, name, category, exports, requiredEvidence]) => ({
    id,
    name,
    category,
    status: "stable",
    tier: "first-party",
    certification: "protected-live",
    auth: ["oauth-m2m", "app-service-principal", "on-behalf-of", "pat"],
    contractClouds: ALL_CLOUDS,
    surfaces: [{ package: "@fabric-harness/databricks", exports }],
    docs: "/docs/databricks/integrations",
    requiredEvidence,
    evidenceIds:
      id === "ai-functions"
        ? ["dbx-cert-d03df5a0a5b69a98d8acb0ad4c1e9ef9bcffefea7e433cbd2ba0ff3d792fec71"]
        : [CURRENT_CONSUMPTION_EVIDENCE_ID],
  }));
}

function inferredUpstreamMaturity(status: DatabricksCapabilityStatus): DatabricksUpstreamMaturity {
  if (status === "beta") return "beta";
  if (status === "stable") return "general-availability";
  return "unknown";
}

function inferredApiFidelity(status: DatabricksCapabilityStatus): DatabricksApiFidelity {
  if (status === "planned") return "not-supported";
  if (status === "partial") return "native-subset";
  return "fabric-abstraction";
}

function inferredLifecycle(checks: readonly string[]): DatabricksLifecycleOperation[] {
  const operations = new Set<DatabricksLifecycleOperation>();
  for (const check of checks) {
    const operation = check.slice(check.lastIndexOf(".") + 1);
    if (/discover|list|status|verify/u.test(operation)) operations.add("discover");
    if (/read|output/u.test(operation)) operations.add("read");
    if (/query/u.test(operation)) operations.add("query");
    if (/execute|invoke|run|deploy|build|consume|write/u.test(operation)) operations.add("execute");
    if (/create/u.test(operation)) operations.add("create");
    if (/mutate|update/u.test(operation)) operations.add("update");
    if (/delete/u.test(operation)) operations.add("delete");
    if (/recover|recovery|restart/u.test(operation)) operations.add("recover");
  }
  return ["discover", "read", "query", "execute", "create", "update", "delete", "recover"].filter(
    (operation): operation is DatabricksLifecycleOperation =>
      operations.has(operation as DatabricksLifecycleOperation),
  );
}

function certifiedCloudsForEvidence(
  evidenceIds: readonly string[] | undefined,
): DatabricksCapabilityCloud[] {
  const clouds = new Set<DatabricksCapabilityCloud>();
  for (const id of evidenceIds ?? []) {
    const evidence = DATABRICKS_CAPABILITY_EVIDENCE.find((candidate) => candidate.id === id);
    for (const environment of evidence?.environments ?? []) clouds.add(environment.cloud);
  }
  return ALL_CLOUDS.filter((cloud) => clouds.has(cloud));
}

export function getDatabricksCapability(id: string): DatabricksCapability | undefined {
  return DATABRICKS_CAPABILITIES.find((capability) => capability.id === id);
}

export function listDatabricksCapabilities(
  options: {
    status?: DatabricksCapabilityStatus;
    tier?: DatabricksSupportTier;
    apiFidelity?: DatabricksApiFidelity;
    domain?: DatabricksPlatformDomainId;
  } = {},
): DatabricksCapability[] {
  const domainCapabilityIds = options.domain
    ? new Set(
        DATABRICKS_PLATFORM_DOMAINS.find((domain) => domain.id === options.domain)?.capabilityIds ??
          [],
      )
    : undefined;
  return DATABRICKS_CAPABILITIES.filter(
    (capability) =>
      (options.status === undefined || capability.status === options.status) &&
      (options.tier === undefined || capability.tier === options.tier) &&
      (options.apiFidelity === undefined ||
        capability.coverage.apiFidelity === options.apiFidelity) &&
      (domainCapabilityIds === undefined || domainCapabilityIds.has(capability.id)),
  );
}

export function missingDatabricksCapabilityEvidence(
  capability: DatabricksCapability,
  evidence: readonly DatabricksCapabilityEvidenceReference[] = DATABRICKS_CAPABILITY_EVIDENCE,
): string[] {
  const linked = new Set(
    (capability.evidenceIds ?? []).flatMap(
      (id) => evidence.find((record) => record.id === id)?.checks ?? [],
    ),
  );
  return capability.requiredEvidence.filter((check) => !linked.has(check));
}
